import React from "react";

// hightchart
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

import styled from "@emotion/styled";

// @mui component
import Box from "../mui/Box";
import Stack from "../mui/Stack";
import Typography from "../mui/Typography";

// Common components
import MagicLoadingBox from "../common/MagicLoadingBox";

interface RegionWiseModalSeriesData {
  name: string;
  data: number[];
  color: string;
}

interface SeriesData {
  regionWiseModalSeriesData: RegionWiseModalSeriesData[];
  categories: string[];
}

interface Props {
  seriesData: SeriesData | {};
  isLoading: boolean;
}

const RegionWiseMedalsGraph: React.FC<Props> = ({ seriesData, isLoading }) => {
  // Conditional destructuring
  let categories: string[] = [];
  let regionWiseModalSeriesData: RegionWiseModalSeriesData[] = [];


  const legendBtns = [
    { color: "#8047E7", label: "Platinum", id: 1 },
    { color: "#E7B147", label: "Gold", id: 2 },
    { color: "#BEBEBE", label: "Silver", id: 3 },
    { color: "#E79447", label: "Bronze", id: 4 },
  ];

  if ("categories" in seriesData && "regionWiseModalSeriesData" in seriesData) {
    ({ categories, regionWiseModalSeriesData } = seriesData);
  }

  // handle Loading state of api data
  if (isLoading) {
    return <MagicLoadingBox height={500} />;
  }

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      backgroundColor: "white",
    },

    title: { text: "" },

    xAxis: {
      categories: categories, // Array of categories for the x-axis
      crosshair: true, // Enables the crosshair
      labels: {
        useHTML: true, // Enable HTML for custom formatting
        formatter: function () {
          const aboveTexts = ["Platinum", "Gold", "Silver", "Bronze"]; // Text to display above categories
          const aboveText = aboveTexts
            .map(
              (text) => `<span style="margin-right: 17px;">${text}</span>` // Add right margin for gap
            )
            .join(""); // Combine spans into a single string

          return `
            <div style="text-align: center;">
              <div style="font-weight: 500; color: #242424; font-size: 13px; margin-bottom: 8px;">
                ${aboveText} <!-- Above text with gaps -->
              </div>
              <div style="display: inline-block; border-radius: 16px; padding: 0px 0px">
              <div style="color: #242424; font-size: 15px; border-radius: 16px; padding: 8px 16px; background-color: #FEF7DA;">
                ${this.value} <!-- Category name -->
              </div>
              </div>
            </div>
          `;
        },
        style: {
          fontSize: "12px", // Default font size
          color: "#4E4E4E", // Default color
        },
      },
      title: {
        text: "", // Optional: Title for the x-axis
      },
      lineColor: "#D7D7D7", // Optional: Line color for the x-axis
      lineWidth: 1, // Optional: Line thickness
    },

    yAxis: {
      min: 0,
      max: 100,
      title: { text: "" },
    },

    tooltip: {
      enabled: true,
      useHTML: true, // Enables custom HTML for the tooltip
      style: {
        fontSize: "12px",
        color: "gray",
        fontWeight: "500",
        textAlign: "center",
      },
      formatter: function () {
        const percentage = this?.point?.percentage ?? 0;
        const count = this?.point?.y ?? 0;
        const category = this?.series?.name ?? "";
        const color = this?.series?.color ?? "#000"; // Default to black if color is not defined
        return `
          <div style="
            display: inline-block;
            position: relative;
            color: #344054;
            border-radius: 8px;
            font-weight: 500;
            padding-bottom:10px;
          ">
           <span style="
          display: inline-block;
          width: 8px;
          height: 8px;
          background-color: ${color};
          border-radius: 50%;
          margin-right: 8px;
        "></span>
            <strong>${category}</strong> <br/>
            Count: ${count} <br/>
            Percentage: ${percentage.toFixed(0)}%<br/>
          </div>`;
      },
      positioner: function (labelWidth, labelHeight, point) {
        let tooltipX = point.plotX + this.chart.plotLeft - labelWidth / 2;
        let tooltipY = point.plotY + this.chart.plotTop - labelHeight - 30; // Add 20px margin from the bottom
        return { x: tooltipX, y: tooltipY };
      },
      
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          useHTML: true,
          style: {
            fontSize: "12px",
            color: "#344054",
            fontWeight: "500",
            textAlign: "center",
          },
          formatter: function () {
            const percentage = this?.point?.percentage ?? 0;
            const count = this?.point?.y ?? 0;
            const category = this?.series?.name ?? "";
            return `
              <div style="
                display: inline-block;
                position: relative;
                background-color: #ffffff;
                color: #344054;
                padding: 5px;
                border-radius: 8px;
                font-weight: 500;
              ">
                ${count} <br/>
                (${percentage.toFixed(0)}%) <br/>
                <div style="
                  content: '';
                  position: absolute;
                  bottom: -8px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 0;
                  height: 0;
                  border-left: 8px solid transparent;
                  border-right: 8px solid transparent;
                  border-top: 8px solid #ffffff;
                "></div>
              </div>`;
          },
        },
      },
    },
    

    series: regionWiseModalSeriesData
      ? JSON.parse(JSON.stringify(regionWiseModalSeriesData))
      : [],
  };
  

  return (
    <Container>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb="15px"
      >
        <StyledGraphHeading> Region wise medals </StyledGraphHeading>

        <StyledLegendContainer>
          {legendBtns?.map((item, index) => (
            <React.Fragment key={index}>
              <StyledLegendBox sx={{ backgroundColor: item.color }} />
              <StyledLegendText>{item.label}</StyledLegendText>
            </React.Fragment>
          ))}
        </StyledLegendContainer>
      </Stack>
      <Box sx={{ height: "2px", backgroundColor: "#D7D7D7", mb: "20px" }} />

      {regionWiseModalSeriesData?.[0]?.data.length !== 0 &&
      regionWiseModalSeriesData?.[1]?.data.length !== 0 ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <Typography>No data found</Typography>
        </Box>
      )}
    </Container>
  );
};

const Container = styled(Stack)({
  width: "100%",
  margin: "0 auto",
  border: "1px solid #D7D7D7",
  marginBottom: "24px",
  padding: "15px",
  borderRadius: "16px",
  backgroundColor: "white",
  boxSizing: "border-box",
  overflow: "hidden",
});

const StyledGraphHeading = styled(Typography)({
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "16.94px",
  mt: "8px",
  mb: "15px",
});

const StyledLegendContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  gap: "8px",
  alignItems: "center",
  maxWidth: "360px",
});

const StyledLegendBox = styled(Box)({
  borderRadius: "16px",
  width: "20px",
  height: "20px",
  padding: "4px 16px",
});

const StyledLegendText = styled(Typography)({
  margin: 0,
  fontSize: "14px",
  color: "#4E4E4E",
  fontWeight: 500,
  lineHeight: "18px",
});

export default RegionWiseMedalsGraph;
